import * as yup from 'yup';
import { TypedDocumentNode } from '@apollo/client';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DistanceUnit, GetTicketQuery as GetTicket } from '__generated__/graphql';

import { ImageInput } from '../../../../common/Common/UploadButton';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { getServerErrors, parseDistance } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useNumberInput from '../../../../common/useNumberInput';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

export interface EditTicketInfoFormProps {
  ticket: Ticket;
  event: Event
  distanceUnits: DistanceUnit[];
  refetchQueries?: TypedDocumentNode[];
}

const editTicketInfoSchema = yup.object().shape({
  title: yup.string().required(),
});

const EditTicketInfoForm = ({
  ticket, event, distanceUnits, refetchQueries = [GetEventLayoutQuery],
}: EditTicketInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const numberInput = useNumberInput();

  const getValues = (ticket: Ticket) => ({
    title: ticket.title,
    title_translations: translationsForAttribute(ticket.translations, 'title'),
    distance: ticket.distance,
    distance_unit: ticket.distance_unit || DistanceUnit.km,
    description: ticket.description,
    description_translations: translationsForAttribute(ticket.translations, 'description'),
    instructions: ticket.instructions,
    instructions_translations: translationsForAttribute(ticket.translations, 'instructions'),
    image: null as File,
    delete_image: false,
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      info: form.values,
    },
    onSuccess: (ticket) => {
      form.reset(getValues(ticket));
      setFormKey((key) => key + 1);
    },
    refetchQueries,
  });

  const { valid, errors } = useValidation({
    schema: editTicketInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'info'),
  });

  const distanceInputHandler = numberInput({ decimals: 3, max: 99_999_999 });

  const handleTitleChange = (title: string) => {
    form.set('title', title);

    if (!form.values.distance || !form.getTouched('distance')) {
      const { distance, unit } = parseDistance(title);

      if (distance && unit) {
        form.set('distance', distance);
        form.set('distance_unit', unit);
      }
    }
  };

  return (
    <UI.Form onSubmit={() => editTicket()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('basic_info')}
          </UI.Legend>
          <UI.InputDescription>
            {t('ticket_basic_info_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="TicketTitle">
            {t('ticket_title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <TranslatableInput
            form={form}
            errors={errors}
            name="title"
            id="TicketTitle"
            defaultLocale={ticket.locale}
            title={t('ticket_title')}
            maxLength={50}
            placeholder={t('ticket_title_placeholder')}
            onChange={(event) => handleTitleChange(event.target.value)}
          />
          <UI.ErrorMessages attribute={t('ticket_title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.distance, touched: form.touched.distance }}>
            <UI.InputLabel htmlFor="distance">
              {t('distance')}
            </UI.InputLabel>
            <UI.Input
              {...form.text('distance', { inputHandler: distanceInputHandler })}
              maxLength={50}
              id="distance"
            />
            <UI.ErrorMessages attribute={t('distance')} errors={errors.distance} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.disance, touched: form.touched.distance }}>
            <UI.InputLabel htmlFor="distance_unit">
              {t('distance_unit')}
            </UI.InputLabel>
            <UI.Select
              {...form.select('distance_unit')}
              id="distance_unit"
            >
              {distanceUnits.map((unit) => (
                <option {...form.option(unit)} key={unit}>
                  {t(`common:distance_units.${unit}`)}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('distance_unit')} errors={errors.distance_unit} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.InputGroup sc={{ valid: !errors.image, touched: form.touched.image }}>
          <UI.InputLabel>
            {t('image')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('sellable_image_description')}
          </UI.InputDescription>
          <ImageInput
            value={form.values.image}
            deleteImage={form.values.delete_image}
            onChange={(file) => {
              form.set('image', file);
              form.set('delete_image', !file);
            }}
            previewImage={ticket.image?.url}
            imageAlt={ticket.title}
            accept=".jpg,.jpeg,.png,.webp"
            width={300}
            ratio={2}
          />
          <UI.ErrorMessages attribute={t('image')} errors={errors.image} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.description, touched: form.touched.description }}>
          <UI.InputLabel id="TicketDescriptionLabel">
            {t('description')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('ticket_description_description')}
          </UI.InputDescription>
          <TranslatableEditor
            form={form}
            errors={errors}
            name="description"
            defaultLocale={ticket.locale}
            title={t('description')}
            attributes={{
              'aria-labelledby': 'TicketDescriptionLabel',
            }}
          />
          <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.instructions, touched: form.touched.instructions }}
        >
          <UI.InputLabel id="TicketInstructionsLabel">
            {t('instructions')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('sellable_instructions_description')}
          </UI.InputDescription>
          <MailEditorLayout
            editor={({ maxContentWidth, ...props }) => (
              <TranslatableEditor
                form={form}
                errors={errors}
                name="instructions"
                defaultLocale={ticket.locale}
                title={t('instructions')}
                attributes={{
                  'aria-labelledby': 'TicketInstructionsLabel',
                }}
                {...props}
              />
            )}
            value={event.confirmation_mail_text}
            event={event}
            instructions={{
              title: form.values.title,
              instructions: form.values.instructions,
            }}
            showRegistrationSummary
            showOrderSummary
          />
          <UI.ErrorMessages attribute={t('instructions')} errors={errors.instructions} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketInfoForm;
